import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createSelector } from 'reselect';
import { toggleCreateCampaignModal } from '../../slices/modals/reducer';
import { useFormik } from "formik";
import * as Yup from "yup";
import classnames from "classnames";
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, Row, Col, Label, FormFeedback, Input, Button, Spinner } from 'reactstrap'
import CustomSelect from '../../Components/Common/CustomSelect';
import { createCampaign } from '../../slices/campaigns/thunk';
import country from "../../common/country";
import { useParams } from 'react-router-dom';
import ErrorAlert from '../../Components/Common/ErrorAlert';
import { resetCreateCampaignErr } from '../../slices/campaigns/reducer';

const CreateCampaignModal = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const selectState = createSelector(state => state, state => ({
    createCampaignModal: state.Modal.createCampaignModal,
    menuData: state.MenuData.client,
    clientPath: state.MenuData.clientPath,
    brandPath: state.MenuData.brandPath,
    marketPath: state.MenuData.marketPath,
    adTypes: state.Campaign.adTypes,
    createCampaignErr: state.Campaign.createCampaignErr,
    createCampaignErrMsg: state.Campaign.createCampaignErrMsg,
  }));
  const { createCampaignModal, menuData, clientPath, brandPath, marketPath, adTypes, createCampaignErr, createCampaignErrMsg } = useSelector(selectState);

  const [clients, setClients] = useState([]);
  const [brands, setBrands] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [bannerTypes, setBannerTypes] = useState([]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: "",
      client: { name: "", value: "" },
      brand: { name: "", value: "" },
      market: { name: "", value: "" },
      adType: { name: "", value: "" },
      bannerType: { name: "", value: null },
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required("Please enter campaign name"),
      client: Yup.object().shape(
        {
          name: Yup.string().required("Please select client"),
          value: Yup.string().required("Please select client")
        }),
      brand: Yup.object().shape({
        name: Yup.string().required("Please select brand"),
        value: Yup.string().required("Please select brand")
      }),
      market: Yup.object().shape({
        name: Yup.string().required("Please select market"),
        value: Yup.string().required("Please select market")
      }),
      adType: Yup.object().shape({
        name: Yup.string().required("Please select ad type"),
        value: Yup.string().required("Please select ad type"),
      }),
      bannerType: Yup.object().shape({
        name: Yup.string().required("Please select banner type"),
        value: Yup.string().required("Please select banner type"),
      }),
    }),
    onSubmit: async (values, { resetForm }) => {
      dispatch(createCampaign({
        name: values.name,
        clientId: values.client.value,
        brandId: values.brand.value,
        marketId: values.market.value,
        adType: values.adType.value,
        bannerType: values.bannerType.value,
        marketIcon: values.market.short,
      }));
      closeModal();
    }
  });

  useEffect(() => {
    if (menuData !== null) {
      setClients(menuData.clients.map(item => { return { name: item.clientName, value: item._id } }));
    }
  }, [menuData]);

  useEffect(() => {
    if (clientPath) {
      const foundClient = menuData.clients.find((client) => client._id === clientPath);
      if (foundClient) {
        handleClientChange({ name: foundClient.clientName, value: foundClient._id });
      }
      if (brandPath) {
        let foundBrand;
        if (Array.isArray(foundClient.brands)) {
          foundBrand = foundClient.brands.find((brand) => brand._id === brandPath);
        } else {
          foundBrand = foundClient.brands;
        }
        if (foundBrand) {
          handleBrandChange({ name: foundBrand.name, value: foundBrand._id });
        }
        if (marketPath) {
          let foundMarket;
          if (Array.isArray(foundBrand?.markets)) {
            foundMarket = foundBrand?.markets.find((market) => market._id === marketPath);
          } else {
            foundMarket = foundBrand?.markets;
          }
          if (foundMarket) {
            const foundCountry = country.find(country => country.name === foundMarket.name);
            handleMarketChange({ name: foundMarket.name, value: foundMarket._id, flagImg: foundCountry.flagImg, short: foundCountry.short });
          }
        }
      }
    }
  }, [createCampaignModal, clientPath, brandPath, marketPath]);

  function handleClick(key) {
    validation.setFieldTouched(key, true);
  }

  function handleChange(key, value) {
    handleClick(key + ".name");
    validation.setFieldValue(key, value);
  }

  function handleReset(key) {
    validation.setFieldValue(key, { name: "", value: "" });
    validation.setFieldTouched(key, false);
  }

  function handleClientChange(item) {
    handleClick("client.name");
    handleChange("client", item);
    const foundClient = menuData.clients.find((client) => client._id === item.value);
    if (foundClient) {
      if (Array.isArray(foundClient.brands)) {
        setBrands(foundClient.brands.map((brand) => { return { name: brand.name, value: brand._id } }));
      } else {
        setBrands({ name: foundClient.brands.name, value: foundClient.brands._id });
      }
    }
    setMarkets([]);
    handleReset('brand');
    handleReset('market');
  }

  function handleBrandChange(item) {
    handleClick("brand.name")
    handleChange("brand", item);
    const foundClient = menuData.clients.find((client) => client._id === validation.values.client.value);
    if (foundClient) {
      if (Array.isArray(foundClient.brands)) {
        const foundBrand = foundClient.brands.find((brand) => brand._id === item.value);
        if (foundBrand) {
          setMarkets(foundBrand.markets.map((market) => {
            const foundCountry = country.find(country => country.name === market.name);
            return { name: market.name, value: market._id, flagImg: foundCountry.flagImg, short: foundCountry.short }
          }));  
        }
      } else {
        const foundBrand = foundClient.brands;
        const foundCountry = country.find(country => country.name === foundBrand.markets.name);
        setMarkets({ name: foundBrand.markets.name, value: foundBrand.markets._id, flagImg: foundCountry.flagImg, short: foundCountry.short });
      }
    }
  }

  function handleMarketChange(item) {
    handleClick("market.name")
    handleChange("market", item);
  }

  function handleAdTypeChange(item) {
    handleClick('adType.name');
    handleChange('adType', item);
    setBannerTypes(item.subTypes)
  }

  function closeModal() {
    dispatch(toggleCreateCampaignModal());
    setTimeout(() => {
      validation.handleReset();
    }, 300)
  }

  return (
    <Modal isOpen={createCampaignModal} toggle={closeModal} centered size='md'>
      <ModalHeader className='p-3' toggle={closeModal}>
        Create Ad
      </ModalHeader>
      <Form onSubmit={(e) => {
        e.preventDefault();
        validation.handleSubmit();
        return false;
      }}>
        <ModalBody className='px-3'>
          <div>
            <ErrorAlert errorState={createCampaignErr} errorMessage={createCampaignErrMsg} resetError={resetCreateCampaignErr} /> 
            <div className='w-100 d-flex justify-content-between'>
              <Label htmlFor="name">Ad Name</Label>
              {validation.touched.name && !validation.errors.name ? (
                <FormFeedback valid>Looks good</FormFeedback>
              ) : null}
              {validation.touched.name && validation.errors.name ? (
                <FormFeedback type="invalid">{validation.errors.name}</FormFeedback>
              ) : null}
            </div>
            <Input type='text'
              name='name'
              id='name'
              className={"form-control " + classnames({ "is-valid": validation.touched.name && !validation.errors.name })}
              placeholder='Enter Ad Name'
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={validation.touched.name && validation.errors.name ? true : false}
              value={validation.values.name || ""}
            />
          </div>
          <div className='mt-3'>
            <div className='w-100 d-flex justify-content-between'>
              <Label htmlFor="client">Select Client</Label>
              {validation.touched.client?.name && !validation.errors.client?.name ? (
                <FormFeedback valid>Looks Good</FormFeedback>
              ) : null}
              {validation.touched.client?.name && validation.errors.client?.name ? (
                <FormFeedback type="invalid">{validation.errors.client.name}</FormFeedback>
              ) : null}
            </div>
            <CustomSelect
              placeholder='Select Client'
              className='form-select'
              validation={classnames({
                "is-valid": validation.touched.client?.name && !validation.errors.client?.name,
                "is-invalid": validation.touched.client?.name && validation.errors.client?.name
              })}
              onChange={(item) => handleClientChange(item)}
              onClick={() => handleClick("client.name")}
              options={clients}
              value={(validation.values.client?.name || '')}
            />
          </div>
          {validation.touched.name && validation.values.name !== "" &&
            validation.touched.client?.name && validation.values.client?.name !== "" ?
            <div className='mt-3'>
              <div className='w-100 d-flex justify-content-between'>
                <Label htmlFor="brand">Select Brand</Label>
                {validation.touched.brand?.name && !validation.errors.brand?.name ? (
                  <FormFeedback valid>Looks good</FormFeedback>
                ) : null}
                {validation.touched.brand?.name && validation.errors.brand?.name ? (
                  <FormFeedback type="invalid">{validation.errors.brand.name}</FormFeedback>
                ) : null}
              </div>
              <CustomSelect
                placeholder='Select Brand'
                className='form-select'
                validation={classnames({
                  "is-valid": validation.touched.brand?.name && !validation.errors.brand?.name,
                  "is-invalid": validation.touched.brand?.name && validation.errors.brand?.name
                })}
                onChange={(item) => handleBrandChange(item)}
                onClick={() => handleClick("brand.name")}
                options={brands}
                value={(validation.values.brand?.name || '')}
              />
            </div>
            : null}
          {validation.touched.name && validation.values.name !== "" &&
            validation.touched.client?.name && validation.values.client?.name !== "" &&
            validation.touched.brand?.name && validation.values.brand?.name !== "" ?
            <div className='mt-3'>
              <div className='w-100 d-flex justify-content-between'>
                <Label htmlFor="market">Select Market</Label>
                {validation.touched.market?.name && !validation.errors.market?.name ? (
                  <FormFeedback valid>Looks good</FormFeedback>
                ) : null}
                {validation.touched.market?.name && validation.errors.market?.name ? (
                  <FormFeedback type="invalid">{validation.errors.market.name}</FormFeedback>
                ) : null}
              </div>
              <CustomSelect
                placeholder='Select Market'
                className='form-select'
                validation={classnames({
                  "is-valid": validation.touched.market?.name && !validation.errors.market?.name,
                  "is-invalid": validation.touched.market?.name && validation.errors.market?.name
                })}
                onChange={(item) => {
                  handleMarketChange(item)
                }}
                onClick={() => handleClick("market.name")}
                options={markets}
                value={(validation.values.market?.name || '')}
              />
            </div> : null}
          {validation.touched.name && validation.values.name !== "" &&
            validation.touched.client?.name && validation.values.client?.name !== "" &&
            validation.touched.brand?.name && validation.values.brand?.name !== "" &&
            validation.touched.market?.name && validation.values.market?.name !== "" ?
            <div className='mt-3'>
              <div className='w-100 d-flex justify-content-between'>
                <Label htmlFor="adType">Ad Type</Label>
                {validation.touched.adType?.name && !validation.errors.adType?.name ? (
                  <FormFeedback valid>Looks good</FormFeedback>
                ) : null}
                {validation.touched.adType?.name && validation.errors.adType?.name ? (
                  <FormFeedback type="invalid">{validation.errors.adType.name}</FormFeedback>
                ) : null}
              </div>
              <CustomSelect
                placeholder='Select Ad Type'
                className='form-select'
                validation={classnames({
                  "is-valid": validation.touched.adType?.name && !validation.errors.adType?.name,
                  "is-invalid": validation.touched.adType?.name && validation.errors.adType?.name
                })}
                onChange={(item) => handleAdTypeChange(item)}
                onClick={() => handleClick("adType.name")}
                options={adTypes}
              />
            </div> : null}
          {validation.touched.name && validation.values.name !== "" &&
            validation.touched.client?.name && validation.values.client?.name !== "" &&
            validation.touched.brand?.name && validation.values.brand?.name !== "" &&
            validation.touched.market?.name && validation.values.market?.name !== "" &&
            validation.touched.adType?.name && validation.values.adType?.name !== "" &&
            <div className='mt-3'>
              <div className='w-100 d-flex justify-content-between'>
                <Label htmlFor="bannerType">Banner Type</Label>
                {validation.touched.bannerType?.name && !validation.errors.bannerType?.name ? (
                  <FormFeedback valid>Looks good</FormFeedback>
                ) : null}
                {validation.touched.bannerType?.name && validation.errors.bannerType?.name ? (
                  <FormFeedback type="invalid">{validation.errors.bannerType.name}</FormFeedback>
                ) : null}
              </div>
              <CustomSelect
                placeholder='Select Banner Type'
                className='form-select'
                validation={classnames({
                  "is-valid": validation.touched.bannerType?.name && !validation.errors.bannerType?.name,
                  "is-invalid": validation.touched.bannerType?.name && validation.errors.bannerType?.name
                })}
                onChange={(item) => handleChange("bannerType", item)}
                onClick={() => handleClick("bannerType.name")}
                options={(bannerTypes || [])}
              />
            </div>
            }
        </ModalBody>
        <ModalFooter className='border-top pt-3 px-3'>
          <Button color="light" onClick={closeModal}>Close</Button>
          <Button color="success" type='submit' disabled={createCampaignErr ? true : false}>
            Add Ad Set
            {createCampaignErr ? <Spinner size='sm' className='ms-2'>Loading...</Spinner> : null}
          </Button>
        </ModalFooter>
      </Form>
    </Modal >
  )
}

export default CreateCampaignModal
